import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "../../store/RootStore";
import type { ActivityDetail, ActivityEvent } from "./ActivityStore";

export interface WebVitalsMetrics {
  LCP: number;
  CLS: number;
  FID: number;
  TTFB: number;
  INP: number;
}

export interface NetworkInfo {
  online: boolean;
  effectiveType: string;
  downlink: number;
  rtt: number;
  saveData: boolean;
}

export interface StorageInfo {
  usage: number;
  quota: number;
  percentUsed: number;
}

export interface CacheStats {
  hits: number;
  misses: number;
  hitRate: number;
}

export interface StoreInfo {
  path: string;
  type: string;
  createdAt: number;
}

export interface SystemSnapshot {
  timestamp: number;
  version: string;
  connection: {
    sseState: string;
    reconnectAttempts: number;
    online: boolean;
    effectiveType: string;
    downlink: number;
    rtt: number;
    saveData: boolean;
  };
  activity: {
    active: ActivityDetail[];
    history: ActivityEvent[];
    counts: {
      queries: number;
      mutations: number;
      processing: number;
    };
  };
  performance: {
    webVitals: WebVitalsMetrics;
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  };
  network: {
    serviceWorker: {
      state: string;
      updateAvailable: boolean;
    };
    cacheStats?: CacheStats;
  };
  stores: {
    active: StoreInfo[];
    count: number;
  };
  storage: StorageInfo;
}

export class SystemMonitorStore {
  isOpen: boolean = false;
  selectedTab: number = 0;

  webVitals: WebVitalsMetrics = {
    LCP: 0,
    CLS: 0,
    FID: 0,
    TTFB: 0,
    INP: 0,
  };

  networkInfo: NetworkInfo = {
    online: navigator.onLine,
    effectiveType: 'unknown',
    downlink: 0,
    rtt: 0,
    saveData: false,
  };

  storageInfo: StorageInfo = {
    usage: 0,
    quota: 0,
    percentUsed: 0,
  };

  cacheStats: CacheStats = {
    hits: 0,
    misses: 0,
    hitRate: 0,
  };

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
    this.initMonitoring();
  }

  private initMonitoring(): void {
    this.monitorNetwork();
    this.updateStorageInfo();

    setInterval(() => {
      this.updateStorageInfo();
    }, 30000);
  }

  private monitorNetwork(): void {
    const updateNetworkInfo = () => {
      runInAction(() => {
        this.networkInfo.online = navigator.onLine;
      });

      const connection = (navigator as any).connection
        || (navigator as any).mozConnection
        || (navigator as any).webkitConnection;

      if (connection) {
        runInAction(() => {
          this.networkInfo.effectiveType = connection.effectiveType || 'unknown';
          this.networkInfo.downlink = connection.downlink || 0;
          this.networkInfo.rtt = connection.rtt || 0;
          this.networkInfo.saveData = connection.saveData || false;
        });
      }
    };

    updateNetworkInfo();

    window.addEventListener('online', updateNetworkInfo);
    window.addEventListener('offline', updateNetworkInfo);

    const connection = (navigator as any).connection;
    if (connection) {
      connection.addEventListener('change', updateNetworkInfo);
    }
  }

  private async updateStorageInfo(): Promise<void> {
    if (!navigator.storage || !navigator.storage.estimate) {
      return;
    }

    try {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;

      runInAction(() => {
        this.storageInfo.usage = usage;
        this.storageInfo.quota = quota;
        this.storageInfo.percentUsed = quota > 0
          ? Math.round((usage / quota) * 100)
          : 0;
      });
    } catch (error) {
      console.error('[SystemMonitorStore] Failed to get storage estimate:', error);
    }
  }

  updateWebVitals(metric: string, value: number): void {
    runInAction(() => {
      switch (metric) {
        case 'LCP':
          this.webVitals.LCP = value;
          break;
        case 'CLS':
          this.webVitals.CLS = value;
          break;
        case 'FID':
          this.webVitals.FID = value;
          break;
        case 'TTFB':
          this.webVitals.TTFB = value;
          break;
        case 'INP':
          this.webVitals.INP = value;
          break;
      }
    });
  }

  toggleMonitor(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.updateStorageInfo();
    }
  }

  setSelectedTab(tabIndex: number): void {
    this.selectedTab = tabIndex;
  }

  async captureSnapshot(): Promise<SystemSnapshot> {
    await this.updateStorageInfo();

    const activityStore = this.rootStore.activityStore;
    const storeRegistry = this.rootStore.storeRegistry;
    const eventSourceStore = this.rootStore.eventSourceStore;

    const activeStores = storeRegistry.allStores.map((store: any) => ({
      path: store.path || 'unknown',
      type: store.constructor.name,
      createdAt: Date.now(),
    }));

    let swState = 'not_supported';
    let swUpdateAvailable = false;

    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          swState = registration.active?.state || 'unknown';
          swUpdateAvailable = !!registration.waiting;
        }
      } catch (error) {
        console.error('[SystemMonitorStore] Failed to get SW registration:', error);
      }
    }

    let memoryInfo;
    if ((performance as any).memory) {
      const memory = (performance as any).memory;
      memoryInfo = {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
      };
    }

    const snapshot: SystemSnapshot = {
      timestamp: Date.now(),
      version: '1.0.0',
      connection: {
        sseState: eventSourceStore.connectionState,
        reconnectAttempts: eventSourceStore.reconnectAttempts,
        online: this.networkInfo.online,
        effectiveType: this.networkInfo.effectiveType,
        downlink: this.networkInfo.downlink,
        rtt: this.networkInfo.rtt,
        saveData: this.networkInfo.saveData,
      },
      activity: {
        active: activityStore.activeOperations,
        history: activityStore.recentEvents,
        counts: {
          queries: activityStore.getActivityCount('query'),
          mutations: activityStore.getActivityCount('mutation'),
          processing: activityStore.getActivityCount('processing'),
        },
      },
      performance: {
        webVitals: { ...this.webVitals },
        memory: memoryInfo,
      },
      network: {
        serviceWorker: {
          state: swState,
          updateAvailable: swUpdateAvailable,
        },
        cacheStats: this.cacheStats.hitRate > 0 ? { ...this.cacheStats } : undefined,
      },
      stores: {
        active: activeStores,
        count: activeStores.length,
      },
      storage: { ...this.storageInfo },
    };

    return snapshot;
  }

  async copySnapshotToClipboard(): Promise<boolean> {
    try {
      const snapshot = await this.captureSnapshot();
      const json = JSON.stringify(snapshot, null, 2);
      await navigator.clipboard.writeText(json);
      return true;
    } catch (error) {
      console.error('[SystemMonitorStore] Failed to copy to clipboard:', error);
      return false;
    }
  }

  async downloadSnapshot(): Promise<void> {
    try {
      const snapshot = await this.captureSnapshot();
      const json = JSON.stringify(snapshot, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `system-snapshot-${timestamp}.json`;

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[SystemMonitorStore] Failed to download snapshot:', error);
    }
  }

  get performanceScore(): number {
    const lcp = this.webVitals.LCP;
    const cls = this.webVitals.CLS;

    if (lcp === 0 && cls === 0) {
      return 0;
    }

    let score = 100;

    if (lcp > 4000) {
      score -= 40;
    } else if (lcp > 2500) {
      score -= 20;
    }

    if (cls > 0.25) {
      score -= 40;
    } else if (cls > 0.1) {
      score -= 20;
    }

    return Math.max(0, score);
  }

  get storageStatus(): 'healthy' | 'warning' | 'critical' {
    const percent = this.storageInfo.percentUsed;
    if (percent >= 90) return 'critical';
    if (percent >= 75) return 'warning';
    return 'healthy';
  }

  get connectionStatus(): 'healthy' | 'warning' | 'error' {
    if (this.rootStore.eventSourceStore.connectionState === 'connected' && this.networkInfo.online) {
      return 'healthy';
    }
    if (this.rootStore.eventSourceStore.connectionState === 'connecting') {
      return 'warning';
    }
    return 'error';
  }
}
